/**
 * useFileSelection — headless multi-select state for "mark several rows → run a
 * bulk action" lists (the Download-as-ZIP UX in Vault / Mobility / TelePortal,
 * citizen + business). Pure logic, no rendering, so every document surface
 * shares one implementation instead of re-deriving a `Set` + toggle by hand.
 */

import { useCallback, useMemo, useState } from 'react';

export interface FileSelection {
  /** The currently-selected ids. */
  selectedIds: Set<string>;
  /** Whether a given id is selected. */
  isSelected: (id: string) => boolean;
  /** Add the id if absent, remove it if present. */
  toggle: (id: string) => void;
  /** Replace the whole selection (e.g. "select all visible"). */
  selectMany: (ids: string[]) => void;
  /** Clear the selection. */
  clear: () => void;
  /** Number of selected ids. */
  count: number;
  /** `true` when at least one id is selected. */
  hasSelection: boolean;
}

export function useFileSelection(): FileSelection {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectMany = useCallback((ids: string[]) => {
    setSelectedIds(new Set(ids));
  }, []);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  return useMemo(
    () => ({
      selectedIds,
      isSelected,
      toggle,
      selectMany,
      clear,
      count: selectedIds.size,
      hasSelection: selectedIds.size > 0,
    }),
    [selectedIds, isSelected, toggle, selectMany, clear]
  );
}
